'use client'

import { ChevronDown, ChevronRight } from 'lucide-react'
import * as React from 'react'

import { ActionCard, type ActionCardTone } from './action-card'
import { Button } from './button'

/**
 * ConfirmCard — pre-write approval surface.
 *
 * Replaces the legacy amber "Approve write action?" block that dumped
 * raw JSON args into a `<pre>`. Same approve/skip semantics, same
 * pendingApproval socket lifecycle, but the card chrome + a
 * human-readable args summary make it look like part of Ordo's UI
 * rather than a debug surface.
 *
 * Resolution is a Promise — caller wires `onApprove` / `onSkip` to the
 * agent's POST `/cards/:id/resolve` (or, in v1, the existing approve
 * endpoint). Once resolved the host swaps `resolved` to true and the
 * card flips to a quiet `ok`/`neutral` tone, so the transcript keeps a
 * remnant of "what got approved" instead of erasing the card.
 *
 * Per `apps/agent/plans/plan-action-cards.md` Phase 1.
 */

export interface ConfirmCardProps {
  /** Tool slug (e.g. `tasker.create_task`) — shown as the attribution badge. */
  slug: string
  /**
   * One-line summary of what's about to happen. Caller derives this
   * from the tool args (e.g. `Run to the market` for create_task).
   * Falls back to a JSON-ish dump if the host can't summarize.
   */
  summary?: string
  /**
   * Optional rationale — the agent's reasoning for proposing this
   * action. Helpful when the action isn't self-explanatory.
   */
  rationale?: string
  /**
   * Detailed args. Rendered as a small <pre> block under the summary
   * for callers who want full transparency. Optional — most cards just
   * need `summary`.
   */
  details?: string
  /** Custom Approve label. Defaults to "Approve". */
  approveLabel?: string
  /** Custom Skip label. Defaults to "Skip". */
  skipLabel?: string
  /**
   * If true, shows the destructive tone + treats Approve as a
   * destructive button. For irreversible writes (delete, send email).
   */
  destructive?: boolean
  /**
   * After the user resolves the card, the host flips this to either
   * 'approved' or 'skipped' so the card stays in the transcript with a
   * dim "remnant" state — what they approved or skipped is still
   * visible, but the action buttons are gone.
   */
  resolution?: 'approved' | 'skipped' | null
  onApprove?: () => void
  onSkip?: () => void
}

export function ConfirmCard({
  slug,
  summary,
  rationale,
  details,
  approveLabel = 'Approve',
  skipLabel = 'Skip',
  destructive,
  resolution,
  onApprove,
  onSkip,
}: ConfirmCardProps) {
  const tone: ActionCardTone =
    resolution === 'approved' ? 'ok' : resolution === 'skipped' ? 'neutral' : destructive ? 'destructive' : 'pending'

  const title =
    resolution === 'approved'
      ? `Approved: ${slug}`
      : resolution === 'skipped'
        ? `Skipped: ${slug}`
        : destructive
          ? 'Confirm — this is destructive'
          : 'Approve write action?'

  return (
    <ActionCard
      title={title}
      description={rationale}
      attribution={slug}
      tone={tone}
      footer={
        resolution ? null : (
          <>
            <Button type="button" size="sm" variant={destructive ? 'destructive' : 'default'} onClick={onApprove}>
              {approveLabel}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onSkip}>
              {skipLabel}
            </Button>
          </>
        )
      }>
      {summary ? <div className="text-sm text-foreground">{summary}</div> : null}
      {details && !resolution ? <ConfirmDetailsDisclosure details={details} /> : null}
    </ActionCard>
  )
}

/**
 * Chevron-toggle disclosure for the JSON `details` block. Default
 * collapsed — most users only need the summary; power users open the
 * panel to read the raw args. Mirrors the chat's `ToolCallsDisclosure`
 * pattern (`apps/agent/client/src/pages/chat.tsx`) so the visual
 * vocabulary stays small.
 */
function ConfirmDetailsDisclosure({ details }: { details: string }) {
  const [open, setOpen] = React.useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground">
        <Chevron className="h-3 w-3 opacity-60" aria-hidden />
        <span>{open ? 'Hide details' : 'Show details'}</span>
      </button>
      {open ? (
        <pre className="mt-1.5 max-h-48 overflow-auto rounded border border-border/50 bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground">
          {details}
        </pre>
      ) : null}
    </div>
  )
}
